import { CSSProperties, ReactElement, ReactNode, useMemo } from 'react';
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowData,
  MRT_SortingState,
  useMantineReactTable,
} from 'mantine-react-table';
import './HubTable.css';

/**
 * The hubs' shared table: one header line, rows divided by a hairline and
 * columns by space alone. Row height comes from the cells, so a two-line cell
 * makes a two-line row and a plain one a one-line row.
 *
 * Built on mantine-react-table like every other queue, so sorting, the sticky
 * header and virtualization come with it. Columns are fixed width unless they
 * set `grow`. `grid-no-grow` would add a spacer column that takes half the slack.
 */

const DIVIDER_COLOR = 'rgba(0, 0, 0, 0.10)';
const ROW_HOVER_COLOR = 'rgba(36, 55, 33, 0.04)';
const ARROW_STYLE: CSSProperties = { color: 'rgba(0, 0, 0, 0.35)', fontSize: 9, marginLeft: 3 };

// Only the sorted column carries an arrow.
const SORT_ICONS = {
  IconArrowsSort: () => null,
  IconSortAscending: () => <span style={ARROW_STYLE}>▲</span>,
  IconSortDescending: () => <span style={ARROW_STYLE}>▼</span>,
};

function headCellStyle(sorted: boolean): CSSProperties {
  return {
    padding: '9px 6px 8px',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: sorted ? 600 : 500,
    color: sorted ? 'var(--mantine-color-black, #000)' : 'rgba(0, 0, 0, 0.55)',
    background: '#fff',
  };
}

const BODY_CELL_STYLE: CSSProperties = { padding: '10px 6px' };

// Column sizes are the prototype's content widths; each cell adds its 6px padding a side.
const CELL_GAP = 12;

export interface HubRowProps {
  onClick?: () => void;
  style?: CSSProperties;
}

export interface HubTableProps<Row extends MRT_RowData> {
  columns: MRT_ColumnDef<Row>[];
  data: Row[];
  loading: boolean;
  initialSorting?: MRT_SortingState;
  /**
   * A row's click and style. `original` is undefined or `{}` on the skeleton
   * rows MRT renders while loading, so read every field off it optionally.
   */
  getRowProps?: (original: Row | undefined) => HubRowProps;
  /** `queue-row-parking` fades a row awaiting Undo; `queue-row-leaving` fades it out. */
  getRowClassName?: (row: Row) => string | undefined;
  /** Replaces the table's default empty message. */
  emptyText?: string;
  /** Shown instead of the empty message, e.g. `LoadFailed` when the read failed (BAR-3125). */
  emptyFallback?: ReactNode;
}

export function HubTable<Row extends MRT_RowData>({
  columns,
  data,
  loading,
  initialSorting = [],
  getRowProps,
  getRowClassName,
  emptyText,
  emptyFallback,
}: HubTableProps<Row>): ReactElement {
  const sizedColumns = useMemo(
    () => columns.map((column) => (column.size ? { ...column, size: column.size + CELL_GAP } : column)),
    [columns]
  );
  const table = useMantineReactTable({
    columns: sizedColumns,
    data,
    layoutMode: 'grid',
    defaultColumn: { grow: false },
    initialState: { density: 'xs' as const, sorting: initialSorting },
    // Skeletons only before the first rows, so each later batch doesn't blank the table.
    state: { isLoading: loading && data.length === 0 },
    icons: SORT_ICONS,
    ...(emptyText ? { localization: { noRecordsToDisplay: emptyText } } : {}),
    ...(emptyFallback ? { renderEmptyRowsFallback: () => emptyFallback } : {}),
    enableStickyHeader: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableRowVirtualization: true,
    enableToolbarInternalActions: false,
    mantinePaperProps: {
      shadow: 'none',
      withBorder: false,
      radius: 0,
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'transparent',
        ['--mrt-base-background-color' as string]: 'var(--ev-color-warm-white, #fafcf7)',
      },
    },
    mantineTableContainerProps: { style: { flex: 1, overflow: 'auto', padding: '0 6px' } },
    mantineTableProps: {
      withRowBorders: true,
      withColumnBorders: false,
      borderColor: DIVIDER_COLOR,
      highlightOnHover: true,
      highlightOnHoverColor: ROW_HOVER_COLOR,
    },
    mantineTableHeadCellProps: ({ column }) => ({
      className: column.getCanSort() ? 'queue-table-sortable-head' : undefined,
      style: headCellStyle(Boolean(column.getIsSorted())),
    }),
    mantineTableBodyCellProps: { style: BODY_CELL_STYLE },
    mantineTableBodyRowProps: ({ row }) => ({
      ...(getRowProps?.(row.original) ?? {}),
      className: row.original ? getRowClassName?.(row.original) : undefined,
    }),
  });

  return <MantineReactTable table={table} />;
}
